<template>
  <div class="auth-panel">
    <template v-if="phase === 'setup'">
      <p class="auth-note">Scan this code with an authenticator app, then enter the 6-digit code it shows.</p>
      <img v-if="qrDataUrl" class="auth-qr" :src="qrDataUrl" alt="Authenticator setup code" />
      <p v-if="groupedSecret" class="auth-note">Or enter this key: <code>{{ groupedSecret }}</code></p>
      <b-form @submit="submitCode">
        <b-form-group label="Authenticator code" label-for="authenticator-code">
          <b-form-input
            id="authenticator-code"
            v-model="code"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            spellcheck="false"
            placeholder="6-digit code"
          />
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="busy">{{ busy ? 'Verifying…' : 'Verify' }}</b-button>
      </b-form>
    </template>
    <template v-else-if="phase === 'challenge'">
      <p class="auth-note">Enter the 6-digit code from your authenticator app, or a recovery code.</p>
      <b-form @submit="submitCode">
        <b-form-group label="Authenticator code" label-for="authenticator-challenge">
          <b-form-input
            id="authenticator-challenge"
            v-model="code"
            type="text"
            autocomplete="one-time-code"
            spellcheck="false"
            placeholder="6-digit code or recovery code"
          />
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="busy">{{ busy ? 'Verifying…' : 'Verify' }}</b-button>
      </b-form>
    </template>
    <template v-else-if="phase === 'recovery'">
      <p class="auth-note">Save these recovery codes. Each one signs you in once if you lose the authenticator. This is the only time they are shown.</p>
      <ul class="recovery-codes">
        <li v-for="entry in recoveryCodes" :key="entry">{{ entry }}</li>
      </ul>
      <div class="recovery-actions">
        <b-button type="button" variant="outline-primary" @click="copyCodes">{{ copied ? 'Copied' : 'Copy codes' }}</b-button>
      </div>
      <label class="recovery-saved">
        <input type="checkbox" v-model="savedCodes" />
        I have saved these codes
      </label>
      <b-button variant="primary" :disabled="!savedCodes" @click="$emit('continue')">Continue</b-button>
    </template>
    <div v-if="errorMessage" class="text-danger mt-2" role="alert">{{ errorMessage }}</div>
  </div>
</template>

<script>
  import QRCode from 'qrcode'

  function groupSecret (secret) {

    return String(secret || '').replace(/(.{4})/g, '$1 ').trim()
  }

  export default {
    props: {
      phase: { type: String, default: 'setup' },
      otpauthUrl: { type: String, default: '' },
      secret: { type: String, default: '' },
      recoveryCodes: { type: Array, default: () => [] },
      errorMessage: { type: String, default: '' },
      busy: { type: Boolean, default: false }
    },
    emits: ['submit', 'continue'],
    data () {

      return {
        code: '',
        qrDataUrl: '',
        copied: false,
        savedCodes: false
      }
    },
    computed: {
      groupedSecret () {

        return groupSecret(this.secret)
      }
    },
    watch: {
      otpauthUrl: {
        immediate: true,
        handler (url) {

          this.drawQr(url)
        }
      }
    },
    methods: {
      async drawQr (url) {

        this.qrDataUrl = ''

        if (!url) return

        try {

          this.qrDataUrl = await QRCode.toDataURL(url, {
            margin: 1,
            width: 220,
            errorCorrectionLevel: 'M'
          })
        } catch (error) {

          this.qrDataUrl = ''
        }
      },
      async copyCodes () {
        const text = (this.recoveryCodes || []).join('\n')
        if (!text) return
        try {
          await navigator.clipboard.writeText(text)
        } catch (error) {
          const area = document.createElement('textarea')
          area.value = text
          document.body.appendChild(area)
          area.select()
          document.execCommand('copy')
          document.body.removeChild(area)
        }
        this.copied = true
      },
      submitCode (event) {

        event.preventDefault()

        if (this.busy) return

        this.$emit('submit', this.code)
      }
    }
  }
</script>

<style scoped>
.auth-panel {
  text-align: left;
}

.auth-note {
  margin-bottom: 0.75rem;
}

.auth-qr {
  display: block;
  width: 220px;
  height: 220px;
  margin-bottom: 0.75rem;
  background: #fff;
}

.recovery-codes {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.recovery-codes li {
  padding: 0.15rem 0;
}

.recovery-actions {
  margin-bottom: 0.75rem;
}

.recovery-saved {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
}
</style>
